import { adminService } from "@/api/admin/admin.service";
import type { TAdminUser, TRoleResponse } from "@/api/admin/admin.types";
import { Badge } from "@/components/ui/badge";
import { Checkbox, useToast } from "@/components/ui";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/Dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { extractErrorMessage } from "@/utils";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

type TUserDetailModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string | null;
  /** Hide role assignment controls and show assigned roles as read-only badges. */
  readOnly?: boolean;
  title?: string;
  description?: string;
};

export function UserDetailModal({
  open,
  onOpenChange,
  userId,
  readOnly = false,
  title = "User Details",
  description = "View user information and activity",
}: TUserDetailModalProps) {
  const { toast } = useToast();
  const [user, setUser] = useState<TAdminUser | null>(null);
  const [roles, setRoles] = useState<TRoleResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [savingRoleName, setSavingRoleName] = useState<string | null>(null);

  useEffect(() => {
    if (open && userId) {
      const fetchUserDetails = async () => {
        setLoading(true);
        setError(null);
        setRolesError(null);
        setUser(null);
        setRoles([]);
        setSavingRoleName(null);

        try {
          const [userResponse, rolesResponse] = await Promise.all([
            adminService.getUser(userId),
            readOnly ? Promise.resolve(null) : adminService.getRoles(),
          ]);

          if (userResponse.error) {
            const errorMessage = await extractErrorMessage(userResponse.error);
            setError(errorMessage);
          } else if (userResponse.data?.data) {
            setUser(userResponse.data.data);
          }

          if (rolesResponse) {
            if (rolesResponse.error) {
              const errorMessage = await extractErrorMessage(rolesResponse.error);
              setRolesError(errorMessage);
            } else if (rolesResponse.data?.data) {
              setRoles(rolesResponse.data.data);
            }
          }
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : "Failed to load user details";
          setError(errorMessage);
        } finally {
          setLoading(false);
        }
      };

      void fetchUserDetails();
    } else {
      setUser(null);
      setRoles([]);
      setError(null);
      setRolesError(null);
      setSavingRoleName(null);
    }
  }, [open, userId, readOnly]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const formatRoleName = (roleName: string) => {
    return roleName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const assignedRoleNames = new Set(user?.roles?.map((role) => role.name) ?? []);

  const visibleRoles = [...roles];
  user?.roles?.forEach((assignedRole) => {
    if (!visibleRoles.some((role) => role.name === assignedRole.name)) {
      visibleRoles.push(assignedRole);
    }
  });

  const updateUserRole = async (role: TRoleResponse, shouldAssign: boolean) => {
    if (!user || savingRoleName) return;

    setSavingRoleName(role.name);
    setRolesError(null);

    const response = shouldAssign
      ? await adminService.assignRole(user.id, role.name)
      : await adminService.revokeRole(user.id, role.name);

    setSavingRoleName(null);

    if (response.error) {
      const errorMessage = await extractErrorMessage(response.error);
      toast({
        variant: "destructive",
        title: shouldAssign ? "Failed to assign role" : "Failed to revoke role",
        description: errorMessage,
      });
      return;
    }

    setUser((currentUser) => {
      if (!currentUser) return currentUser;

      const currentRoles = currentUser.roles ?? [];
      const nextRoles = shouldAssign
        ? currentRoles.some((currentRole) => currentRole.name === role.name)
          ? currentRoles
          : [...currentRoles, role]
        : currentRoles.filter((currentRole) => currentRole.name !== role.name);

      return {
        ...currentUser,
        roles: nextRoles,
      };
    });

    toast({
      variant: "success",
      title: shouldAssign ? "Role assigned" : "Role revoked",
      description: `${formatRoleName(role.name)} ${shouldAssign ? "was added to" : "was removed from"} this user.`,
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-800">{error}</div>
        ) : user ? (
          <div className="space-y-4">
            {/* User Information */}
            <Card>
              <CardHeader>
                <CardTitle>User Information</CardTitle>
                <CardDescription>Basic user profile details</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Name</label>
                    <p className="text-sm text-foreground">
                      {user.first_name} {user.last_name}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Email</label>
                    <p className="text-sm text-foreground">{user.email}</p>
                  </div>
                  {user.phone_number && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Phone Number</label>
                      <p className="text-sm text-foreground">{user.phone_number}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Status</label>
                    <p className="text-sm text-foreground">{user.status_id === 1 ? "Active" : "Inactive"}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Email Verified</label>
                    <p className="text-sm text-foreground">{user.email_verified ? "Yes" : "No"}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Phone Verified</label>
                    <p className="text-sm text-foreground">{user.phone_verified ? "Yes" : "No"}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Two Factor Enabled</label>
                    <p className="text-sm text-foreground">{user.two_factor_enabled ? "Yes" : "No"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Roles */}
            <Card>
              <CardHeader>
                <CardTitle>Roles</CardTitle>
                <CardDescription>
                  {readOnly ? "Roles assigned to this user" : "Assign or revoke user roles"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {rolesError && <div className="mb-3 rounded-md bg-red-50 p-3 text-sm text-red-800">{rolesError}</div>}
                {readOnly ? (
                  <div className="flex flex-wrap gap-1.5">
                    {(user.roles?.length ? user.roles.map((role) => role.name) : [user.role || "user"]).map(
                      (roleName) => (
                        <Badge
                          key={roleName}
                          variant="secondary"
                        >
                          {formatRoleName(roleName ?? "")}
                        </Badge>
                      )
                    )}
                  </div>
                ) : visibleRoles.length > 0 ? (
                  <div className="space-y-2">
                    {visibleRoles.map((role) => {
                      const isAssigned = assignedRoleNames.has(role.name);
                      const isSaving = savingRoleName === role.name;

                      return (
                        <label
                          key={role.role_id}
                          className="flex cursor-pointer items-start gap-3 rounded bg-muted p-2"
                        >
                          <Checkbox
                            checked={isAssigned}
                            disabled={Boolean(savingRoleName)}
                            onCheckedChange={(checked) => {
                              void updateUserRole(role, checked === true);
                            }}
                            className="mt-0.5"
                            aria-label={`${isAssigned ? "Revoke" : "Assign"} ${formatRoleName(role.name)} role`}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-foreground">{formatRoleName(role.name)}</p>
                              {isSaving && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                            </div>
                            {role.description && <p className="text-xs text-muted-foreground">{role.description}</p>}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{formatRoleName(user.role || "user")}</p>
                )}
              </CardContent>
            </Card>

            {/* Account Information */}
            <Card>
              <CardHeader>
                <CardTitle>Account Information</CardTitle>
                <CardDescription>Account creation and activity dates</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Created At</label>
                    <p className="text-sm text-foreground">{formatDate(user.created_at)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Updated At</label>
                    <p className="text-sm text-foreground">{formatDate(user.updated_at)}</p>
                  </div>
                  {user.last_login && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Last Login</label>
                      <p className="text-sm text-foreground">{formatDate(user.last_login)}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
